import React, { useContext, useEffect, useMemo, useState } from 'react';
import userPermissionContext from '../../../context/userPermissionContext';
import { KTSVG } from '../../../_metronic/helpers';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const toNum = (v, d = 0) => { const n = Number(v); return Number.isFinite(n) ? n : d; };
const extractArray = (payload, key) => {
	if (!payload) return [];
	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload?.data)) return payload.data;
	if (key && Array.isArray(payload?.[key])) return payload[key];
	if (key && Array.isArray(payload?.data?.[key])) return payload.data[key];
	const first = Object.values(payload || {}).find(v => Array.isArray(v));
	if (first) return first;
	if (payload?.data && typeof payload.data === 'object') {
		const nested = Object.values(payload.data).find(v => Array.isArray(v));
		if (nested) return nested;
	}
	return [];
};

const RENEWAL_KEYS = [
	{ label: 'Tax Token (Light Vehicle)', field: 'lvTaxTokenCount' },
	{ label: 'Tax Token (Motor Cycle)', field: 'mcTaxTokenCount' },
	{ label: 'Fitness (Light Vehicle)', field: 'lvFitnessCount' },
	{ label: 'Route Permit (Light Vehicle)', field: 'lvRoutePermitCount' },
	{ label: 'Route Permit (Motor Cycle)', field: 'mcRoutePermitCount' },
];

/* ── UI Core Components ── */
const StatCard = React.memo(({ icon, label, value, color = '#1e293b', bg = '#fff', borderColor = '#e2e8f0', sub }) => (
	<div className="stat-card" style={{ border: `1px solid ${borderColor}`, background: bg }}>
		<div className="stat-card-header">
			{icon && <span style={{ fontSize: 18 }}>{icon}</span>}
			<span className="stat-card-label">{label}</span>
		</div>
		<div className="stat-card-value" style={{ color }}>{value}</div>
		{sub && <div className="stat-card-sub">{sub}</div>}
	</div>
));

const MiniStat = React.memo(({ label, value, color = '#1e293b', bg = '#f8fafc' }) => (
	<div className="mini-stat" style={{ background: bg }}>
		<div className="mini-stat-value" style={{ color }}>{value}</div>
		<div className="mini-stat-label" style={{ color }}>{label}</div>
	</div>
));

const BarRow = React.memo(({ label, value, max, color, showPercent = false }) => {
	const pct = max > 0 ? ((value / max) * 100) : 0;
	return (
		<div className="bar-row">
			<div className="bar-row-header">
				<span className="bar-row-label">{label}</span>
				<span className="bar-row-value">{value}{showPercent ? ` (${pct.toFixed(0)}%)` : ''}</span>
			</div>
			<div className="bar-track">
				<div className="bar-fill" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
			</div>
		</div>
	);
});

const SectionTitle = React.memo(({ icon, title }) => (
	<div className="section-title">
		<span style={{ fontSize: 16 }}>{icon}</span> {title}
	</div>
));

const PanelCard = React.memo(({ title, children, linkHref, linkLabel }) => (
	<div className="panel-card">
		<div className="panel-card-header">
			<span className="panel-card-title">{title}</span>
			{linkHref && <a href={`#${linkHref}`} className="panel-card-link">{linkLabel || 'View →'}</a>}
		</div>
		<div className="panel-card-body">{children}</div>
	</div>
));

const AlertRow = React.memo(({ label, count }) => (
	<div className="alert-row">
		<span className="alert-row-label">{label}</span>
		{count > 0
			? <span className="alert-badge alert-badge-danger">{count}</span>
			: <span className="alert-check">✓</span>
		}
	</div>
));

/* ── Extracted Feature Components ── */
const FleetOverview = React.memo(({ fleet, loading }) => {
	const V = v => loading ? <span style={{ color: '#cbd5e1' }}>—</span> : v;
	return (
		<>
			<SectionTitle icon="🚗" title="Fleet Overview" />
			<div className='row g-3 mb-4'>
				<div className='col-6 col-md-4 col-xl'>
					<StatCard icon="📊" label="Total Active" value={V(toNum(fleet.totalActiveVehicles))} color="#1e293b" />
				</div>
				<div className='col-6 col-md-4 col-xl'>
					<StatCard icon="🏢" label="In-House" value={V(toNum(fleet.inHouseVehicleCount ?? fleet.inHouseVehcileCount))} color="#1e40af" borderColor="#bfdbfe" bg="#f0f7ff" />
				</div>
				<div className='col-6 col-md-4 col-xl'>
					<StatCard icon="🤝" label="Third-Party" value={V(toNum(fleet.thirdPartyVehicleCount))} color="#6d28d9" borderColor="#ddd6fe" bg="#f5f3ff" />
				</div>
			</div>

			<SectionTitle icon="🏷️" title="Vehicle Type Breakdown" />
			<div className='row g-3 mb-4'>
				<div className='col-lg-6'>
					<PanelCard title="🚙 Light Vehicle">
						<div className="panel-card-padding" style={{ display: 'flex', gap: 12 }}>
							<MiniStat label="Active" value={V(toNum(fleet.activeLightVehicle))} color="#0369a1" bg="#f0f9ff" />
							<MiniStat label="In-House" value={V(toNum(fleet.inHouseLightVehicle))} color="#1e40af" bg="#eff6ff" />
							<MiniStat label="Third-Party" value={V(toNum(fleet.thirdPartyLightVehicle))} color="#6d28d9" bg="#f5f3ff" />
						</div>
					</PanelCard>
				</div>
				<div className='col-lg-6'>
					<PanelCard title="🏍️ Motor Cycle">
						<div className="panel-card-padding" style={{ display: 'flex', gap: 12 }}>
							<MiniStat label="Active" value={V(toNum(fleet.activeMotorCycle))} color="#b45309" bg="#fffbeb" />
							<MiniStat label="In-House" value={V(toNum(fleet.inHouseMotorCycle))} color="#92400e" bg="#fef3c7" />
							<MiniStat label="Third-Party" value={V(toNum(fleet.thirdPartyMotorCycle))} color="#c2410c" bg="#fff7ed" />
						</div>
					</PanelCard>
				</div>
			</div>
		</>
	);
});

const OperationsDriver = React.memo(({ extStats, loading }) => {
	const V = v => loading ? <span style={{ color: '#cbd5e1' }}>—</span> : v;
	return (
		<>
			<SectionTitle icon="⚙️" title="Operations & Driver" />
			<div className='row g-3 mb-4'>
				<div className='col-lg-3'>
					<PanelCard title="🔧 Functional Status">
						<div className="panel-card-padding">
							{(() => {
								const r = toNum(extStats?.functionalStatus?.running);
								const rr = toNum(extStats?.functionalStatus?.runningReserved);
								const b = toNum(extStats?.functionalStatus?.breakdown);
								const total = r + rr + b || 1;
								return <>
									<BarRow label="Running" value={r} max={total} color="#16a34a" showPercent />
									<BarRow label="Reserved" value={rr} max={total} color="#f59e0b" showPercent />
									<BarRow label="Breakdown" value={b} max={total} color="#dc2626" showPercent />
								</>;
							})()}
						</div>
					</PanelCard>
				</div>
				<div className='col-lg-3'>
					<PanelCard title="👤 Driver Overview">
						<div className="panel-card-padding">
							<div style={{ textAlign: 'center', marginBottom: 10 }}>
								<div style={{ fontSize: 30, fontWeight: 700, color: '#1e293b' }}>{V(toNum(extStats?.driverStats?.totalActive))}</div>
								<div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>Total Active Drivers</div>
							</div>
							<div style={{ display: 'flex', gap: 8 }}>
								<MiniStat label="Assigned" value={V(toNum(extStats?.driverStats?.assigned))} color="#166534" bg="#f0fdf4" />
								<MiniStat label="Unassigned" value={V(toNum(extStats?.driverStats?.unassigned))} color="#dc2626" bg="#fef2f2" />
							</div>
							<div style={{ marginTop: 10, borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>
								<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
									<span style={{ color: '#475569' }}>🚘 Self-Driven</span>
									<span style={{ fontWeight: 700, color: '#0369a1' }}>{V(toNum(extStats?.selfDriven?.selfDriven))}</span>
								</div>
								<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
									<span style={{ color: '#475569' }}>👨‍✈️ Driver-Assigned</span>
									<span style={{ fontWeight: 700, color: '#6d28d9' }}>{V(toNum(extStats?.selfDriven?.driverAssigned))}</span>
								</div>
							</div>
						</div>
					</PanelCard>
				</div>
				<div className='col-lg-3'>
					<PanelCard title="🔄 Usage Type">
						<div className="panel-card-padding" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
							<MiniStat label="Pool" value={V(toNum(extStats?.carUseType?.pool))} color="#0ea5e9" bg="#f0f9ff" />
							<MiniStat label="Individual" value={V(toNum(extStats?.carUseType?.individual))} color="#8b5cf6" bg="#f5f3ff" />
							<MiniStat label="Sharing" value={V(toNum(extStats?.carUseType?.sharing))} color="#f59e0b" bg="#fffbeb" />
						</div>
					</PanelCard>
				</div>
				<div className='col-lg-3'>
					<PanelCard title="📋 Rental Type">
						<div className="panel-card-padding" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
							<MiniStat label="Rental (Full)" value={V(toNum(extStats?.rentalType?.rentalCarFull))} color="#16a34a" bg="#f0fdf4" />
							<MiniStat label="Rental (Body)" value={V(toNum(extStats?.rentalType?.rentalCarBody))} color="#0369a1" bg="#f0f9ff" />
							<MiniStat label="Not Applicable" value={V(toNum(extStats?.rentalType?.notApplicable))} color="#64748b" bg="#f8fafc" />
						</div>
					</PanelCard>
				</div>
			</div>
		</>
	);
});

const AlertsCompliance = React.memo(({ extStats, expiryExpired, expirySoon, loading }) => {
	const V = v => loading ? <span style={{ color: '#cbd5e1' }}>—</span> : v;
	const expiredTotal = useMemo(() => RENEWAL_KEYS.reduce((s, k) => s + toNum(expiryExpired?.[k.field]), 0), [expiryExpired]);
	const soonTotal = useMemo(() => RENEWAL_KEYS.reduce((s, k) => s + toNum(expirySoon?.[k.field]), 0), [expirySoon]);

	return (
		<>
			<SectionTitle icon="⚠️" title="Alerts & Compliance" />
			<div className='row g-3 mb-4'>
				<div className='col-lg-4'>
					<PanelCard title="🚨 Vehicle Alerts">
						<div>
							<AlertRow label="No Driver Assigned" count={toNum(extStats?.unassigned?.noDriver)} />
							<AlertRow label="No Project Assigned" count={toNum(extStats?.unassigned?.noProject)} />
							<AlertRow label="Missing Tax Token Date" count={toNum(extStats?.missingDocs?.noTaxToken)} />
							<AlertRow label="Missing Fitness Date" count={toNum(extStats?.missingDocs?.noFitness)} />
							<AlertRow label="Missing Route Permit Date" count={toNum(extStats?.missingDocs?.noRoutePermit)} />
						</div>
					</PanelCard>
				</div>
				<div className='col-lg-4'>
					<PanelCard title="📄 Document Renewals" linkHref="/to-do-list" linkLabel="To-do list →">
						<table className='table table-sm mb-0' style={{ fontSize: 12 }}>
							<thead><tr className="table-header">
								<th style={{ paddingLeft: 14 }}>Document</th>
								<th className='text-center' style={{ color: '#dc2626' }}>Expired</th>
								<th className='text-center' style={{ color: '#d97706' }}>Due 30d</th>
								<th className='text-center'>Total</th>
							</tr></thead>
							<tbody>
								{RENEWAL_KEYS.map(({ label, field }) => {
									const exp = toNum(expiryExpired?.[field]);
									const soon = toNum(expirySoon?.[field]);
									return (
										<tr key={field}>
											<td style={{ paddingLeft: 14 }}>{label}</td>
											<td className='text-center'>
												{exp > 0 ? <span className="alert-badge alert-badge-danger">{exp}</span> : '0'}
											</td>
											<td className='text-center'>
												{soon > 0 ? <span className="alert-badge alert-badge-warning">{soon}</span> : '0'}
											</td>
											<td className='text-center fw-bold'>{exp + soon}</td>
										</tr>
									);
								})}
								<tr style={{ background: '#f8fafc', fontWeight: 600 }}>
									<td style={{ paddingLeft: 14 }}>Total</td>
									<td className='text-center'>{expiredTotal > 0 ? <span className="alert-badge alert-badge-danger">{expiredTotal}</span> : '0'}</td>
									<td className='text-center'>{soonTotal > 0 ? <span className="alert-badge alert-badge-warning">{soonTotal}</span> : '0'}</td>
									<td className='text-center'>{expiredTotal + soonTotal}</td>
								</tr>
							</tbody>
						</table>
					</PanelCard>
				</div>
				<div className='col-lg-4'>
					<PanelCard title="📊 Document Summary">
						<div className="panel-card-padding">
							<div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
								<div style={{ flex: 1, background: expiredTotal > 0 ? '#fef2f2' : '#f0fdf4', borderRadius: 8, padding: '14px 10px', textAlign: 'center', border: `1px solid ${expiredTotal > 0 ? '#fecaca' : '#bbf7d0'}` }}>
									<div style={{ fontSize: 26, fontWeight: 700, color: expiredTotal > 0 ? '#dc2626' : '#16a34a' }}>{V(expiredTotal)}</div>
									<div style={{ fontSize: 10, fontWeight: 600, color: expiredTotal > 0 ? '#dc2626' : '#16a34a' }}>Expired</div>
								</div>
								<div style={{ flex: 1, background: soonTotal > 0 ? '#fffbeb' : '#f0fdf4', borderRadius: 8, padding: '14px 10px', textAlign: 'center', border: `1px solid ${soonTotal > 0 ? '#fde68a' : '#bbf7d0'}` }}>
									<div style={{ fontSize: 26, fontWeight: 700, color: soonTotal > 0 ? '#d97706' : '#16a34a' }}>{V(soonTotal)}</div>
									<div style={{ fontSize: 10, fontWeight: 600, color: soonTotal > 0 ? '#d97706' : '#16a34a' }}>Due in 30 Days</div>
								</div>
							</div>
							<div style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8' }}>
								Total documents needing attention: <span style={{ fontWeight: 700, color: '#1e293b' }}>{expiredTotal + soonTotal}</span>
							</div>
						</div>
					</PanelCard>
				</div>
			</div>
		</>
	);
});

const ExpenseOverview = React.memo(({ expenseSummary, headCost, extStats, activeV, loading }) => {
	const V = v => loading ? <span style={{ color: '#cbd5e1' }}>—</span> : v;
	const expTotal = toNum(expenseSummary.totalCost);
	const expVariance = toNum(expenseSummary.costVariancePercentage);
	const costPerVehicle = activeV > 0 ? Math.round(expTotal / activeV) : 0;

	return (
		<>
			<SectionTitle icon="💰" title="Expense Overview" />
			<div className='row g-3 mb-4'>
				<div className='col-6 col-md-4'>
					<StatCard icon="💵" label="Total Expense (Period)" value={V(`৳${expTotal.toLocaleString()}`)}
						color="#1e293b" sub={!loading && expVariance !== 0 ? `${expVariance > 0 ? '↑' : '↓'} ${Math.abs(expVariance)}% vs prior period` : undefined} />
				</div>
				<div className='col-6 col-md-4'>
					<StatCard icon="📈" label="Avg Cost / Vehicle" value={V(`৳${costPerVehicle.toLocaleString()}`)}
						color="#1e293b" sub={`Based on ${activeV} active vehicles`} />
				</div>
				<div className='col-12 col-md-4'>
					<PanelCard title="🏆 Top 5 Expense Vehicles">
						<div style={{ maxHeight: 180, overflowY: 'auto' }}>
							<table className='table table-sm mb-0' style={{ fontSize: 12 }}>
								<tbody>
									{(extStats?.topExpenseVehicles || []).map((v, i) => {
										const topMax = toNum((extStats?.topExpenseVehicles || [])[0]?.totalExpense) || 1;
										const exp = toNum(v?.totalExpense);
										return (
											<tr key={i}>
												<td style={{ paddingLeft: 14, width: 24, fontWeight: 700, color: i === 0 ? '#f59e0b' : '#94a3b8' }}>{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`}</td>
												<td style={{ fontWeight: 600 }}>{v?.vehicleNumber || '-'}</td>
												<td className='text-end' style={{ fontWeight: 700, color: '#1e293b' }}>৳{exp.toLocaleString()}</td>
												<td style={{ width: 60 }}>
													<div className="bar-track" style={{ height: 5 }}>
														<div className="bar-fill" style={{ width: `${(exp / topMax) * 100}%`, background: '#dc2626' }} />
													</div>
												</td>
											</tr>
										);
									})}
									{!loading && (!extStats?.topExpenseVehicles || extStats.topExpenseVehicles.length === 0) && <tr><td colSpan={4} className='text-muted text-center py-2'>No data</td></tr>}
								</tbody>
							</table>
						</div>
					</PanelCard>
				</div>
			</div>
			<div className='row g-3 mb-4'>
				<div className='col-12'>
					<PanelCard title="💳 Expense Head Wise Cost" linkHref="/report-vehicleExpenseReport" linkLabel="Expense report →">
						<div style={{ maxHeight: 250, overflowY: 'auto' }}>
							<table className='table table-sm mb-0' style={{ fontSize: 12 }}>
								<thead><tr className="table-header">
									<th style={{ paddingLeft: 14 }}>Head</th><th className='text-end'>Amount (৳)</th><th style={{ width: 120 }}>Share</th>
								</tr></thead>
								<tbody>
									{(headCost || []).map((r, i) => {
										const cost = toNum(r?.totalCost ?? r?.total ?? r?.amount ?? r?.cost ?? r?.expenseAmount);
										const share = expTotal > 0 ? (cost / expTotal) * 100 : 0;
										return (
											<tr key={`h-${i}`}>
												<td style={{ paddingLeft: 14 }}>{r?.head_name || r?.headName || '-'}</td>
												<td className='text-end fw-bold'>{cost.toLocaleString()}</td>
												<td><div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
													<div className="bar-track" style={{ height: 5, width: '100%' }}>
														<div className="bar-fill" style={{ width: `${Math.min(share, 100)}%`, background: '#94a3b8' }} />
													</div>
													<span style={{ fontSize: 10, color: '#94a3b8', minWidth: 30 }}>{share.toFixed(0)}%</span>
												</div></td>
											</tr>
										);
									})}
									{!loading && (!headCost || headCost.length === 0) && <tr><td colSpan={3} className='text-muted text-center py-3'>No data</td></tr>}
								</tbody>
							</table>
						</div>
					</PanelCard>
				</div>
			</div>
		</>
	);
});

const FleetComposition = React.memo(({ extStats, loading }) => {
	return (
		<>
			<SectionTitle icon="🔍" title="Fleet Composition" />
			<div className='row g-3 mb-4'>
				<div className='col-lg-4'>
					<PanelCard title="⛽ Fuel Type">
						<div className="panel-card-padding">
							{(() => {
								const fuels = extStats?.fuelTypes || [];
								const fuelMax = fuels.length > 0 ? Math.max(...fuels.map(x => toNum(x?.count))) : 1;
								const colors = ['#0ea5e9', '#f59e0b', '#16a34a', '#8b5cf6', '#dc2626', '#d97706', '#64748b'];
								return fuels.map((f, i) => (
									<BarRow key={i} label={f?.fuelType || 'Unknown'} value={toNum(f?.count)} max={fuelMax} color={colors[i % colors.length]} />
								));
							})()}
							{!loading && (!extStats?.fuelTypes || extStats.fuelTypes.length === 0) && <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: 12 }}>No data</div>}
						</div>
					</PanelCard>
				</div>
				<div className='col-lg-4'>
					<PanelCard title="📅 Vehicle Age">
						<div className="panel-card-padding">
							{(() => {
								const ages = extStats?.ageDistribution || [];
								const ageMax = ages.length > 0 ? Math.max(...ages.map(x => toNum(x?.count))) : 1;
								const colors = ['#16a34a', '#0ea5e9', '#f59e0b', '#d97706', '#dc2626'];
								return ages.map((a, i) => (
									<BarRow key={i} label={a?.ageGroup} value={toNum(a?.count)} max={ageMax} color={colors[i % colors.length]} />
								));
							})()}
							{!loading && (!extStats?.ageDistribution || extStats.ageDistribution.length === 0) && <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: 12 }}>No data</div>}
						</div>
					</PanelCard>
				</div>
				<div className='col-lg-4'>
					<PanelCard title="🏷️ Top Brands">
						<div className="panel-card-padding">
							{(() => {
								const brands = extStats?.topBrands || [];
								const brandMax = brands.length > 0 ? Math.max(...brands.map(x => toNum(x?.count))) : 1;
								return brands.map((b, i) => (
									<BarRow key={i} label={b?.brandName || 'Unknown'} value={toNum(b?.count)} max={brandMax} color="#8b5cf6" />
								));
							})()}
							{!loading && (!extStats?.topBrands || extStats.topBrands.length === 0) && <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: 12 }}>No data</div>}
						</div>
					</PanelCard>
				</div>
			</div>
		</>
	);
});

const ProjectsVendors = React.memo(({ projectVehicle, categoryVehicle, extStats, loading }) => {
	return (
		<>
			<SectionTitle icon="🏗️" title="Projects & Vendors" />
			<div className='row g-3 mb-4'>
				<div className='col-lg-7'>
					<PanelCard title="📁 Project Wise Vehicles" linkHref="/report-buWiseLightVehicle" linkLabel="Report →">
						<div style={{ maxHeight: 300, overflowY: 'auto' }}>
							<table className='table table-sm mb-0' style={{ fontSize: 12 }}>
								<thead>
									<tr className="table-header">
										<th rowSpan={2} style={{ paddingLeft: 14, verticalAlign: 'middle' }}>Project</th>
										<th colSpan={2} className='text-center' style={{ borderBottom: '1px solid #e2e8f0', color: '#0369a1' }}>Light Vehicle</th>
										<th colSpan={2} className='text-center' style={{ borderBottom: '1px solid #e2e8f0', color: '#b45309' }}>Motor Cycle</th>
										<th rowSpan={2} className='text-center' style={{ verticalAlign: 'middle' }}>Total</th>
									</tr>
									<tr className="table-header">
										<th className='text-center' style={{ color: '#166534', fontSize: 10 }}>Active</th>
										<th className='text-center' style={{ color: '#64748b', fontSize: 10 }}>Inactive</th>
										<th className='text-center' style={{ color: '#166534', fontSize: 10 }}>Active</th>
										<th className='text-center' style={{ color: '#64748b', fontSize: 10 }}>Inactive</th>
									</tr>
								</thead>
								<tbody>
									{(projectVehicle || []).map((r, i) => {
										const lvAct = toNum(r?.lightVehicleActive);
										const lvInact = toNum(r?.lightVehicleInactive);
										const mcAct = toNum(r?.motorCycleActive);
										const mcInact = toNum(r?.motorCycleInactive);
										const tot = toNum(r?.totalVehicleCount) || (lvAct + lvInact + mcAct + mcInact);
										return (
											<tr key={`p-${i}`}>
												<td style={{ paddingLeft: 14 }}>{r?.projectName || r?.project_name || '-'}</td>
												<td className='text-center'>{lvAct}</td>
												<td className='text-center'>{lvInact}</td>
												<td className='text-center'>{mcAct}</td>
												<td className='text-center'>{mcInact}</td>
												<td className='text-center fw-bold'>{tot}</td>
											</tr>
										);
									})}
									{!loading && (!projectVehicle || projectVehicle.length === 0) && <tr><td colSpan={6} className='text-muted text-center py-3'>No data</td></tr>}
								</tbody>
							</table>
						</div>
					</PanelCard>
				</div>
				<div className='col-lg-5'>
					<PanelCard title="🤝 Vendor-wise Third Party">
						<div style={{ maxHeight: 300, overflowY: 'auto' }}>
							<table className='table table-sm mb-0' style={{ fontSize: 12 }}>
								<thead><tr className="table-header">
									<th style={{ paddingLeft: 14 }}>Vendor</th><th className='text-center'>Vehicles</th><th style={{ width: 90 }}>Share</th>
								</tr></thead>
								<tbody>
									{(extStats?.vendorWise || []).map((v, i) => {
										const vendorTotal = (extStats?.vendorWise || []).reduce((s, x) => s + toNum(x?.count), 0) || 1;
										const share = (toNum(v?.count) / vendorTotal) * 100;
										return (
											<tr key={i}>
												<td style={{ paddingLeft: 14 }}>{v?.vendorName || 'Unknown'}</td>
												<td className='text-center fw-bold'>{toNum(v?.count)}</td>
												<td><div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
													<div className="bar-track" style={{ height: 5, width: '100%' }}>
														<div className="bar-fill" style={{ width: `${Math.min(share, 100)}%`, background: '#6d28d9' }} />
													</div>
													<span style={{ fontSize: 10, color: '#94a3b8', minWidth: 28 }}>{share.toFixed(0)}%</span>
												</div></td>
											</tr>
										);
									})}
									{!loading && (!extStats?.vendorWise || extStats.vendorWise.length === 0) && <tr><td colSpan={3} className='text-muted text-center py-3'>No data</td></tr>}
								</tbody>
							</table>
						</div>
					</PanelCard>
				</div>
			</div>
			<div className='row g-3'>
				<div className='col-12'>
					<PanelCard title="📂 Category Wise Vehicles" linkHref="/report-typeWiseVehicle" linkLabel="Report →">
						<div style={{ maxHeight: 300, overflowY: 'auto' }}>
							<table className='table table-sm mb-0' style={{ fontSize: 12 }}>
								<thead><tr className="table-header">
									<th style={{ paddingLeft: 14 }}>Category</th><th className='text-center'>Active</th><th className='text-center'>Inactive</th><th className='text-center'>Total</th>
								</tr></thead>
								<tbody>
									{(categoryVehicle || []).map((r, i) => {
										const a = toNum(r?.totalActiveVehicles);
										const ia = toNum(r?.totalInactiveVehicles);
										const t = toNum(r?.totalVehicle) || a + ia;
										return (
											<tr key={`c-${i}`}>
												<td style={{ paddingLeft: 14 }}>{r?.categoryName || r?.category_name || 'Others'}</td>
												<td className='text-center'>{a}</td>
												<td className='text-center'>{ia}</td>
												<td className='text-center fw-bold'>{t}</td>
											</tr>
										);
									})}
									{!loading && (!categoryVehicle || categoryVehicle.length === 0) && <tr><td colSpan={4} className='text-muted text-center py-3'>No data</td></tr>}
								</tbody>
							</table>
						</div>
					</PanelCard>
				</div>
			</div>
		</>
	);
});


/* ── Main Data Controller Component ── */
function ManagementDashboard() {
	const { jwt, businessUnit } = useContext(userPermissionContext);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	
	// State
	const [fleet, setFleet] = useState({});
	const [expiryExpired, setExpiryExpired] = useState({});
	const [expirySoon, setExpirySoon] = useState({});
	const [headCost, setHeadCost] = useState([]);
	const [expenseSummary, setExpenseSummary] = useState({});
	const [projectVehicle, setProjectVehicle] = useState([]);
	const [categoryVehicle, setCategoryVehicle] = useState([]);
	const [extStats, setExtStats] = useState({});
	const [lastSync, setLastSync] = useState(null);

	useEffect(() => {
		let cancelled = false;
		const token = jwt?.token;
		if (!token) return;
		const headers = { Authorization: `Bearer ${token}` };
		const pid = businessUnit?.idProjects;
		const qp = pid != null && pid !== '' ? `?currentProject=${encodeURIComponent(pid)}&projectId=${encodeURIComponent(pid)}` : '';
		const get = (url) => fetch(url, { headers }).then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)));

		(async () => {
			try {
				setLoading(true); setError('');
				const res = await get(`${API_URL}vms_app_dashboard_summary${qp}`);
				
				if (cancelled) return;
				const d = res.data || {};
				
				setFleet(d.fleet || {});
				setExpiryExpired(d.expiryExpired || {});
				setExpirySoon(d.expirySoon || {});
				
				const expSum = d.expenseSummary || {};
				setHeadCost(extractArray(expSum, 'expenseHeadWiseTotalCost'));
				setExpenseSummary(expSum);
				
				setProjectVehicle(extractArray(d, 'projectVehicle'));
				setCategoryVehicle(extractArray(d, 'categoryVehicle'));
				setExtStats(d.extStats || {});
				setLastSync(new Date());
			} catch { if (!cancelled) setError('Failed to load dashboard.'); }
			finally { if (!cancelled) setLoading(false); }
		})();
		return () => { cancelled = true; };
	}, [jwt?.token, businessUnit?.idProjects]);

	return (
		<div className="dashboard-container">
			<div className="dashboard-header">
				<div className="dashboard-header-title">
					<KTSVG path='/media/svg/icons/Layout/Layout-4-blocks.svg' className='svg-icon-1 svg-icon-primary' />
					<span className="dashboard-header-text">Fleet Management Dashboard</span>
					{businessUnit?.projectName && <span className="dashboard-header-project">— {businessUnit.projectName}</span>}
				</div>
				<span className="dashboard-header-time">{lastSync ? `Synced ${lastSync.toLocaleTimeString()}` : 'Loading...'}</span>
			</div>

			<div className="dashboard-body">
				{error && <div className='alert alert-warning py-2 mb-3' style={{ fontSize: 12 }}>{error}</div>}

				<FleetOverview fleet={fleet} loading={loading} />
				<OperationsDriver extStats={extStats} loading={loading} />
				<AlertsCompliance extStats={extStats} expiryExpired={expiryExpired} expirySoon={expirySoon} loading={loading} />
				<ExpenseOverview expenseSummary={expenseSummary} headCost={headCost} extStats={extStats} activeV={toNum(fleet.totalActiveVehicles)} loading={loading} />
				<FleetComposition extStats={extStats} loading={loading} />
				<ProjectsVendors projectVehicle={projectVehicle} categoryVehicle={categoryVehicle} extStats={extStats} loading={loading} />
			</div>
		</div>
	);
}

export default ManagementDashboard;
